import { useCallback, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { IconButton, Menu, Text } from 'react-native-paper';

import { CheckSmallIcon, SwapVertIcon } from '@/assets';
import { useCommentSortStore } from '@/features/posts/store/commentSortStore';
import { CommentSortEnum } from '@/features/posts/types/commentSort';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { ExtendedMenu } from '@/shared/components/ExtendedMenu';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = {
  count?: number;
};

export const CommentsHeader: FC<Props> = ({ count }) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'screens.post-detail'
  });

  const [sortMenuVisible, setSortMenuVisible] = useState(false);
  const openMenu = () => setSortMenuVisible(true);
  const closeMenu = () => setSortMenuVisible(false);

  const sort = useCommentSortStore((state) => state.sort);
  const setSort = useCommentSortStore((state) => state.setSort);

  const handleSortChange = useCallback(
    (newSort: CommentSortEnum) => {
      setSort(newSort);
      closeMenu();
    },
    [setSort]
  );

  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      <Text variant="titleLarge">{t('comments', { count })}</Text>
      <ExtendedMenu
        visible={sortMenuVisible}
        onDismiss={closeMenu}
        anchor={
          <IconButton
            style={{ margin: -8 }}
            icon={() => (
              <SwapVertIcon
                width={24}
                height={24}
                fill={theme.colors.primary}
              />
            )}
            onPress={openMenu}
          />
        }
      >
        {Object.values(CommentSortEnum).map((sortValue) => (
          <Menu.Item
            key={sortValue}
            title={t(`commentSort.${sortValue}`)}
            onPress={() => handleSortChange(sortValue)}
            containerStyle={{ justifyContent: 'space-between' }}
            trailingIcon={({ color, size }) =>
              sort === sortValue && (
                <CheckSmallIcon fill={color} width={size} height={size} />
              )
            }
          />
        ))}
      </ExtendedMenu>
    </View>
  );
};
